import Styles from './styles.module.css';
import '../../salon/auth/login/tokens.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import NoIndex from '../../components/NoIndex';
import {
  IoHelpCircleOutline,
  IoLogInOutline,
  IoPeopleOutline,
  IoChatbubbleEllipsesOutline,
  IoClipboardOutline,
  IoSettingsOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoExpandOutline,
  IoBulbOutline,
  IoArrowUpOutline,
  IoCompassOutline,
  IoAlertCircleOutline,
  IoBookmarkOutline,
  IoDocumentTextOutline,
  IoCalendarOutline,
  IoListOutline,
  IoPersonAddOutline,
  IoMailOutline,
  IoMegaphoneOutline,
  IoImageOutline,
  IoPricetagOutline,
} from 'react-icons/io5';

/*
 * 企業(サロン)管理画面の使い方マニュアル(ログイン不要で開けるヘルプページ)。
 * school/student と同じ構成・ルールで作成(docs/ai/manual-pattern.md 参照)。
 *
 * 画面のスクリーンショットは public/salon-manual/ に置き、/salon-manual/xxx.jpg で参照する
 * (社内のUX確認用テスト企業アカウントで撮影したもの。学生は実在しない「TEST専門学校」の
 * テストデータ)。
 *
 * 対象は「応募者管理」(15.evaluation)・「応募設定」(15.evaluation/settings)・「審査」(16.judging)・
 * 「設定・管理」(8.setting)の4画面。6.manage は src/salon/desktop/index.tsx でインポート・ルートが
 * コメントアウトされており、本番では到達できない画面のため対象外にしている(docs/ai/07-change-impact-map.md
 * 確認・2026-10-20 確認)。
 */

type Section = {
  id: string;
  title: string;
  icon: React.ReactNode;
};

const ZoomContext = React.createContext<(src: string, alt: string) => void>(() => {});

type Hotspot = { x: number; y: number; n: number };

// ── caption を書くかどうかのルール(StepList との重複を防ぐため) ──
// 1. この Shot の直前(同じ SubHeading 内)に StepList があり、かつ hotspots を付けている場合、
//    caption に手順をもう一度書かない。「①…②…」のように StepList の文言をなぞる書き出しは
//    禁止。caption は省略するか、StepList に書いていない新情報だけを書く。
// 2. StepList が無い単独の Shot(操作の起点ではなく結果画面・状態の対比だけを見せるもの)には、
//    画面が何かを示す caption を必ずつける。
// 3. 判断に迷ったら「StepList を声に出して読んだときに、caption と同じ内容を2回言っていないか」
//    を基準にする。
const Shot: React.FC<{ src: string; alt: string; caption?: string; hotspots?: Hotspot[] }> = ({ src, alt, caption, hotspots }) => {
  const openZoom = useContext(ZoomContext);
  return (
    <figure className={Styles.shot}>
      <button type="button" className={Styles.shotButton} onClick={() => openZoom(src, alt)} aria-label={`${alt}を拡大表示`}>
        <img src={src} alt={alt} loading="lazy" />
        {hotspots?.map((h, i) => (
          <span key={`${h.n}-${i}`} className={Styles.hotspot} style={{ left: `${h.x}%`, top: `${h.y}%` }}>{h.n}</span>
        ))}
        <span className={Styles.shotZoomHint}><IoExpandOutline /></span>
      </button>
      {caption && <figcaption className={Styles.shotCaption}>{caption}</figcaption>}
    </figure>
  );
};

const IconItem: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className={Styles.iconItem}>
    <span className={Styles.iconItemIcon}>{icon}</span>
    <div className={Styles.iconItemBody}>
      <p className={Styles.iconItemTitle}>{title}</p>
      <p className={Styles.iconItemDesc}>{children}</p>
    </div>
  </div>
);

const FeatureBox: React.FC<{ title?: string; children: React.ReactNode }> = ({ title = '機能', children }) => (
  <div className={Styles.featureBox}>
    <p className={Styles.featureBoxTitle}>{title}</p>
    <div className={Styles.iconList}>{children}</div>
  </div>
);

const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.tipBox}>
    <IoBulbOutline className={Styles.tipBoxIcon} />
    <div className={Styles.tipBoxBody}>
      <p className={Styles.tipBoxTitle}>活用のヒント</p>
      <ul className={Styles.tipBoxList}>{children}</ul>
    </div>
  </div>
);

const StepList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ol className={Styles.stepList}>{children}</ol>
);
const Step: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className={Styles.step}>{children}</li>
);

const Caution: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.caution}>
    <IoAlertCircleOutline className={Styles.cautionIcon} />
    <div>{children}</div>
  </div>
);

const SectionLead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={Styles.sectionLead}>{children}</p>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className={Styles.subHeading}>{children}</h3>
);

const SECTIONS: Section[] = [
  { id: 'intro', title: 'はじめに', icon: <IoHelpCircleOutline /> },
  { id: 'login', title: 'ログインと画面の見方', icon: <IoLogInOutline /> },
  { id: 'list', title: '応募者管理', icon: <IoPeopleOutline /> },
  { id: 'detail', title: 'メッセージ・ステータス・メモ', icon: <IoChatbubbleEllipsesOutline /> },
  { id: 'salonPr', title: 'サロンPR(会社説明会・クーポン・スタッフ)', icon: <IoMegaphoneOutline /> },
  { id: 'settings', title: '応募設定', icon: <IoClipboardOutline /> },
  { id: 'judging', title: '審査', icon: <IoClipboardOutline /> },
  { id: 'company', title: '会社の設定・管理', icon: <IoSettingsOutline /> },
  { id: 'faq', title: 'よくある質問', icon: <IoHelpCircleOutline /> },
];

const GOALS: { label: string; id: string }[] = [
  { label: 'ログインできない', id: 'login' },
  { label: '応募者・サロン見学希望者の一覧を見たい', id: 'list' },
  { label: '応募者とメッセージのやり取りをしたい', id: 'detail' },
  { label: '選考のステータスを変更したい', id: 'detail' },
  { label: '会社説明会・クーポンを掲載したい', id: 'salonPr' },
  { label: '審査員を招待して評価してもらいたい', id: 'settings' },
  { label: '審査員として評価を提出したい', id: 'judging' },
  { label: '会社名・ロゴ・通知先を変更したい', id: 'company' },
  { label: 'うまくいかない・困っている', id: 'faq' },
];

const SalonManual: React.FC = () => {
  const [ activeId, setActiveId ] = useState<string>(SECTIONS[0].id);
  const [ menuOpen, setMenuOpen ] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [ zoom, setZoom ] = useState<{ src: string; alt: string } | null>(null);
  const openZoom = (src: string, alt: string) => setZoom({ src, alt });

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoom(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const goTo = (id: string) => {
    setMenuOpen(false);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const [ showBackToTop, setShowBackToTop ] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <ZoomContext.Provider value={openZoom}>
      <NoIndex title="企業管理画面の使い方 | memorii works" />
      <div className={Styles.page}>
        <div className={Styles.mobileBar}>
          <span className={Styles.mobileBarTitle}>企業管理画面マニュアル</span>
          <button
            type="button"
            className={Styles.menuButton}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="目次を開く">
            {menuOpen ? <IoCloseOutline /> : <IoMenuOutline />}
          </button>
        </div>

        <div className={Styles.layout}>
          <nav className={`${Styles.toc} ${menuOpen ? Styles.toc_open : ''}`}>
            <p className={Styles.tocHeading}>目次</p>
            <ul className={Styles.tocList}>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={activeId === s.id ? `${Styles.tocItem} ${Styles.tocItem_active}` : Styles.tocItem}
                    onClick={() => goTo(s.id)}>
                    <span className={Styles.tocIcon}>{s.icon}</span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          {menuOpen && <div className={Styles.tocOverlay} onClick={() => setMenuOpen(false)} />}

          <main className={Styles.content}>
            <header className={Styles.pageHeader}>
              <h1 className={Styles.pageTitle}>企業管理画面の使い方</h1>
              <p className={Styles.pageLead}>
                サロン(企業)のご担当者向けに、memorii works の企業管理画面のうち
                「応募者管理」「応募設定」「審査」「会社の設定・管理」の使い方をまとめたマニュアルです。
                <a
                  href="https://memorii-works.com/company/login"
                  target="_blank"
                  rel="noreferrer"
                  className={Styles.inlineCode}>
                  ログイン画面
                </a>
                の「企業」タブからログインします。パソコン・スマートフォンのどちらからでもご覧いただけます。
              </p>
              <p className={Styles.pageUpdated}>最終更新日：2026年10月20日</p>
            </header>

            <nav className={Styles.goalBox} aria-label="やりたいことから探す">
              <p className={Styles.goalBoxTitle}>
                <IoCompassOutline className={Styles.goalBoxIcon} />
                やりたいことから探す
              </p>
              <div className={Styles.goalList}>
                {GOALS.map((g, i) => (
                  <button key={`${g.id}-${i}`} type="button" className={Styles.goalItem} onClick={() => goTo(g.id)}>
                    {g.label}
                  </button>
                ))}
              </div>
            </nav>

            <section id="intro" ref={(el) => (sectionRefs.current['intro'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHelpCircleOutline className={Styles.sectionIcon} />
                はじめに
              </h2>
              <SectionLead>
                企業管理画面は、御社に届いた応募・サロン見学希望を管理し、学生とメッセージでやり取りするための画面です。
              </SectionLead>
              <p>
                学生が使う memorii アプリと連動しており、学生がアプリ上で応募・サロン見学の申し込みをすると、
                その内容が「応募者管理」に反映されます。選考が進むごとにステータスを変更し、必要に応じて
                審査員に評価を依頼できます。
              </p>

              <SubHeading>はじめて使うときの流れ</SubHeading>
              <StepList>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('login')}>
                    「ログインと画面の見方」
                  </button>
                  を確認してログインします。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('list')}>
                    「応募者管理」
                  </button>
                  で、届いている応募・サロン見学希望を確認します。
                </Step>
                <Step>
                  審査員に評価を依頼する場合は、先に
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('settings')}>
                    「応募設定」
                  </button>
                  で審査項目・審査員を登録しておきます。
                </Step>
              </StepList>

              <p>
                画面左のメニューは契約内容によって表示が変わります。「応募者管理」「審査」は、応募者管理の
                機能をご利用の企業にのみ表示されます。
              </p>
            </section>

            <section id="login" ref={(el) => (sectionRefs.current['login'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoLogInOutline className={Styles.sectionIcon} />
                ログインと画面の見方
              </h2>
              <SectionLead>発行されたメールアドレス・パスワードでログインし、左のメニューで画面を切り替えます。</SectionLead>

              <StepList>
                <Step>ログイン画面で「企業」タブを選びます。</Step>
                <Step>登録済みのメールアドレスとパスワードを入力します。</Step>
                <Step>「ログインする」を押します。</Step>
              </StepList>

              <Caution>
                審査員として招待された方は、同じログイン画面から届いたメールアドレス・初期パスワードで
                ログインすると、審査専用の画面(「審査」タブのみ)が表示されます。
              </Caution>

              <TipBox>
                <li>左のメニュー項目の横にある「？」アイコンを押すと、その画面の簡単な説明がその場で読めます。</li>
              </TipBox>
            </section>

            <section id="list" ref={(el) => (sectionRefs.current['list'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoPeopleOutline className={Styles.sectionIcon} />
                応募者管理
              </h2>
              <SectionLead>サロン見学・応募の学生の選考状況を一覧で管理する画面です。</SectionLead>

              <SubHeading>一覧の見方</SubHeading>
              <p>
                一覧は「サロン見学」と「応募」の2種類の学生をまとめて表示します。学生名・進捗状況(ステータス)・
                学校名・申込日時・メモが並び、行の右側のアイコンから保存(ブックマーク)・メッセージを開けます。
              </p>
              <Shot
                src="/salon-manual/list-full.jpg"
                alt="応募者管理の一覧画面(種別・学生名・進捗状況・学校名・申込日時・メモの列)"
                caption="進捗状況は選考段階・内定承諾・辞退・不採用・見学済みなどが色分けされたバッジで表示される" />

              <SubHeading>絞り込む・並び替える</SubHeading>
              <StepList>
                <Step>上部のタブ(すべて・クーポン・会社説明会・サロン見学・応募)で種別を絞り込みます。応募は募集回ごとにタブが分かれます。</Step>
                <Step>クーポン・会社説明会・サロン見学の各タブでは、さらに「全段階」「申込」「予約確定」など、進み具合ごとの小さなタブで絞り込めます。</Step>
                <Step>「フリーワード検索」で、学生名などから絞り込めます。</Step>
                <Step>見出しの「進捗状況」をクリックすると、進み具合の順に並び替えられます。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/salon-manual/list-overview.jpg"
                  alt="応募タブを選んだ状態(募集回ごとのタブ・選考段階ごとの件数)"
                  caption="応募タブは募集回(第1回・第2回…)ごとに分かれ、さらに選考段階ごとの件数が表示される" />
                <Shot
                  src="/salon-manual/list-coupon.jpg"
                  alt="クーポンタブを選んだ状態(進み具合ごとの絞り込み)"
                  caption="クーポン・会社説明会・サロン見学タブでは、進み具合(申込・予約確定・利用済みなど)でさらに絞り込める" />
              </div>

              <FeatureBox>
                <IconItem icon={<IoBookmarkOutline />} title="保存(ブックマーク)">
                  一覧の保存アイコンを押すと、その学生に印をつけられます。優先して対応したい学生の目印として使えます。
                </IconItem>
                <IconItem icon={<IoMailOutline />} title="メッセージ">
                  一覧のメッセージアイコンを押すと、その学生との個別のやり取り画面が開きます(詳しい使い方は次の章)。
                </IconItem>
                <IconItem icon={<IoCalendarOutline />} title="募集回を締める">
                  募集回を複数設定している場合、「第n回を締める」ボタンでその回の受付を終了できます(設定は
                  「応募設定」の募集回で行います)。
                </IconItem>
              </FeatureBox>

              <TipBox>
                <li>進捗状況で並び替えておくと、選考が止まっている学生を見つけて優先的に連絡するのに使えます。</li>
                <li>対応が必要な学生に保存(ブックマーク)の印をつけておくと、他の学生に紛れず見つけやすくなります。</li>
              </TipBox>
            </section>

            <section id="detail" ref={(el) => (sectionRefs.current['detail'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoChatbubbleEllipsesOutline className={Styles.sectionIcon} />
                メッセージ・ステータス・メモ
              </h2>
              <SectionLead>一覧で学生の行をクリックすると、個別のやり取り画面が右側に開きます。</SectionLead>
              <p>
                詳細画面の上部には選考ステップ(1次選考・2次選考…・採用／不採用／辞退)が並び、下に
                「メッセージ」「My Card」「履歴書」「管理・集計」の4つのタブがあります(サロン見学の学生には
                「履歴書」タブはありません)。
              </p>
              <Shot
                src="/salon-manual/detail-message.jpg"
                alt="応募者の詳細画面(選考ステップ・メッセージタブ)"
                caption="選考ステップの下にメッセージ・My Card・履歴書・管理・集計のタブが並ぶ" />

              <SubHeading>ステータスを変更する</SubHeading>
              <StepList>
                <Step>選考ステップの、進めたい段階(例: 「3次選考」)をクリックします。</Step>
                <Step>メッセージの入力欄に、その段階用のテンプレート文章が自動で入ります(「応募設定」で設定した内容。空に設定した段階では何も入りません)。</Step>
                <Step>内容を確認・編集し、「送信」を押すと学生に送られます。「取り消す」を押すとテンプレートの挿入を取り消せます。</Step>
              </StepList>
              <Shot
                src="/salon-manual/status-change-template.jpg"
                alt="ステータス変更時にメッセージ入力欄へテンプレートが入った状態"
                hotspots={[{ n: 3, x: 88, y: 90 }]} />
              <Caution>
                テンプレートを入力欄に入れただけでは学生に送信されません。ステータス自体は選考ステップを
                クリックした時点で変わりますが、メッセージは必ず「送信」を押してください。
              </Caution>

              <SubHeading>メッセージをやり取りする</SubHeading>
              <StepList>
                <Step>入力欄にメッセージを入力し、「送信」を押します。</Step>
                <Step>画面右上の「未読にする」(既読の場合)を押すと、あとで返信したいメッセージを未読に戻せます。</Step>
              </StepList>
              <TipBox>
                <li>学生がサロン見学・会社説明会・クーポンを申し込むと、専用のカード形式でメッセージ内に表示されます。</li>
                <li>返信が必要なメッセージは「未読にする」で未読に戻しておくと、あとで見落としにくくなります。</li>
              </TipBox>

              <SubHeading>メモを使う</SubHeading>
              <p>学生には表示されない、社内向けのメモです。一覧のメモ欄、または詳細画面の鉛筆アイコンから書けます。</p>
              <StepList>
                <Step>メモ欄(一覧)またはメモの鉛筆アイコン(詳細画面)をクリックします。</Step>
                <Step>内容を入力し、「保存」を押します。</Step>
              </StepList>
              <Shot
                src="/salon-manual/memo-modal.jpg"
                alt="メモの編集モーダル"
                hotspots={[{ n: 2, x: 56, y: 64 }]} />

              <SubHeading>My Card・履歴書・管理・集計タブ</SubHeading>
              <FeatureBox>
                <IconItem icon={<IoPeopleOutline />} title="My Card">
                  学生のプロフィール(名前・学校名・卒業予定など)を確認できます。
                </IconItem>
                <IconItem icon={<IoDocumentTextOutline />} title="履歴書">
                  応募時に学生が提出した履歴書を、実際の書式に近い形で確認できます(サロン見学のみの学生には表示されません)。
                </IconItem>
                <IconItem icon={<IoClipboardOutline />} title="管理・集計">
                  選考段階を選んで、その段階に審査員が提出した評価(◎○△×・総合判断・コメント)をまとめて確認できます。
                </IconItem>
              </FeatureBox>
              <Shot
                src="/salon-manual/detail-mycard.jpg"
                alt="My Cardタブ(学生のプロフィール)"
                caption="My Cardタブ。名前・学校・卒業予定に加え、理想の美容師像やサロンへの要望(希望勤務エリアなど)も確認できる" />
              <Shot
                src="/salon-manual/detail-resume-filled.jpg"
                alt="履歴書タブ(応募者が提出した履歴書)"
                caption="履歴書タブ。氏名・連絡先・学歴などが実際の履歴書に近いレイアウトで表示され、「大きく表示する」「PDF書き出し」もできる" />
              <Shot
                src="/salon-manual/detail-management.jpg"
                alt="管理・集計タブ(選考段階ごとの審査員評価)"
                caption="管理・集計タブ。選考段階を選ぶと、その段階で審査員が提出した評価が一覧で確認できる" />
            </section>

            <section id="salonPr" ref={(el) => (sectionRefs.current['salonPr'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoMegaphoneOutline className={Styles.sectionIcon} />
                サロンPR(会社説明会・クーポン・スタッフ)
              </h2>
              <SectionLead>会社説明会・クーポン・スタッフプロフィール・作品写真を掲載し、美容学生にサロンの魅力を発信する画面です。</SectionLead>
              <p>
                左メニューの「サロンPR」を開くと、「作品・写真管理」「会社説明会」「クーポン」「スタッフプロフィール」の
                4つのタブがあります。会社説明会・クーポンに学生が申し込むと、「応募者管理」の一覧にもその種別
                (会社説明会・クーポン)で表示されます(確認のしかたは「応募者管理」の章を参照)。
              </p>

              <SubHeading>会社説明会を掲載する</SubHeading>
              <StepList>
                <Step>「会社説明会」タブを開き、「説明会を追加する」を押します。</Step>
                <Step>説明会名・説明・開始時刻・説明会の時間・場所・定員を入力します(説明は任意、他は必須)。</Step>
                <Step>「追加」を押します。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/salon-manual/salonpr-session-list.jpg"
                  alt="会社説明会タブの一覧画面"
                  hotspots={[{ n: 1, x: 91, y: 26 }]} />
                <Shot
                  src="/salon-manual/salonpr-session-modal.jpg"
                  alt="会社説明会の追加フォーム"
                  hotspots={[{ n: 2, x: 50, y: 26 }, { n: 3, x: 60, y: 11 }]} />
              </div>
              <p>
                追加した説明会は「受付中」として一覧に表示されます。「受付を締め切る」を押すと、それ以上の
                申し込みを受け付けなくなります。
              </p>

              <SubHeading>クーポンを掲載する</SubHeading>
              <StepList>
                <Step>「クーポン」タブを開き、「クーポンを追加する」を押します。</Step>
                <Step>施術画像・施術名・通常料金・美容学生料金を入力します(補足・説明は任意)。</Step>
                <Step>「追加」を押します。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/salon-manual/salonpr-coupon-list.jpg"
                  alt="クーポンタブの一覧画面"
                  caption="通常料金に取り消し線を引いた形で、美容学生料金が強調して表示される"
                  hotspots={[{ n: 1, x: 91, y: 26 }]} />
                <Shot
                  src="/salon-manual/salonpr-coupon-modal.jpg"
                  alt="クーポンの追加フォーム"
                  hotspots={[{ n: 2, x: 50, y: 49 }, { n: 3, x: 60, y: 11 }]} />
              </div>

              <SubHeading>スタッフプロフィールを掲載する</SubHeading>
              <StepList>
                <Step>「スタッフプロフィール」タブを開き、「スタッフを追加する」を押します。</Step>
                <Step>プロフィール画像・名前・役職(任意)・一言(任意)・Instagram ID(任意)などを入力します。</Step>
                <Step>「追加」を押します。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/salon-manual/salonpr-staff-list.jpg"
                  alt="スタッフプロフィールタブの一覧画面"
                  caption="登録したスタッフにはInstagram・TikTokのアイコンも表示される(入力した場合)"
                  hotspots={[{ n: 1, x: 91, y: 26 }]} />
                <Shot
                  src="/salon-manual/salonpr-staff-modal.jpg"
                  alt="スタッフの追加フォーム"
                  hotspots={[{ n: 2, x: 50, y: 49 }, { n: 3, x: 60, y: 11 }]} />
              </div>

              <FeatureBox>
                <IconItem icon={<IoImageOutline />} title="作品・写真管理">
                  サロンの施術写真などを掲載するタブです(操作は会社説明会・クーポンと同様、一覧から追加します)。
                </IconItem>
                <IconItem icon={<IoPricetagOutline />} title="種別ごとの「？」アイコン">
                  各タブの見出し横にある「？」を押すと、そのタブの簡単な説明がその場で読めます。
                </IconItem>
              </FeatureBox>

              <TipBox>
                <li>会社説明会・クーポンは、学生がアプリで求人ページを見たときの「サロンを知ってもらう・来店してもらう」ための入り口として使えます。</li>
                <li>スタッフプロフィールで人柄が伝わると、学生が応募・見学を決める後押しになります。</li>
              </TipBox>
            </section>

            <section id="settings" ref={(el) => (sectionRefs.current['settings'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoClipboardOutline className={Styles.sectionIcon} />
                応募設定
              </h2>
              <SectionLead>「応募者管理」画面右上の「応募設定」から、募集回・審査項目・ステータス変更時のメッセージ・審査員を設定します。</SectionLead>
              <p className={Styles.footnote}>
                募集回・審査項目・ステータス変更時のメッセージは、変更したら「設定を保存」を押して保存します。
                審査員管理は、追加・削除した時点ですぐに保存されます。
              </p>

              <SubHeading>募集回</SubHeading>
              <p>
                締切日を入れると、締切日の翌日以降に届いた応募は自動で次の募集回(第2回募集など)に入ります。
                締切日を入れなければ、同じ募集回のまま受け付け続けます。
              </p>
              <Shot
                src="/salon-manual/settings-periods-stages.jpg"
                alt="応募設定の募集回・選考段階の審査項目"
                caption="募集回ごとに受付中・締切済みの状態が表示され、選考段階ごとに審査項目のタイトルを設定できる" />

              <SubHeading>選考段階・サロン見学の審査項目</SubHeading>
              <StepList>
                <Step>選考段階(1次〜5次選考)ごとに、評価基準の数(0〜8)を選びます。</Step>
                <Step>項目数分の入力欄が開くので、審査項目のタイトルを入力します。</Step>
                <Step>サロン見学の審査項目も同様に設定します(選考段階と違い1ラウンドのみ)。</Step>
              </StepList>
              <p>ここで設定した項目数・タイトルが、「審査」画面で審査員が入力する評価項目になります。</p>
              <Shot
                src="/salon-manual/settings-stage-detail.jpg"
                alt="選考段階ごとの審査項目の設定(1次〜4次選考の例)"
                caption="段階ごとに評価基準の数と項目名を個別に設定できる(未設定の段階は「評価基準を設定しない」のままでよい)" />

              <SubHeading>サロン見学フォーム</SubHeading>
              <p>
                学生がサロン見学を申し込む際の事前フォームを設定できます。見学希望日・気になる項目・希望店舗を
                聞くかどうかを、それぞれオン/オフで切り替えられます(切り替えた時点で保存されます)。
              </p>
              <Shot
                src="/salon-manual/settings-visitform.jpg"
                alt="サロン見学フォーム設定とサロン見学の審査項目"
                caption="見学希望日・気になる項目・希望店舗の入力有無を個別にオン/オフできる" />

              <SubHeading>ステータス変更時のメッセージ</SubHeading>
              <p>
                「応募者管理」の詳細画面でステータスを変えたときに、メッセージの入力欄へ自動で入る文章を
                ステータスごとに設定できます。本文を空にしたステータスには何も入りません。
              </p>
              <Shot
                src="/salon-manual/settings-messages.jpg"
                alt="ステータス変更時のメッセージの設定一覧"
                caption="応募・サロン見学・会社説明会・クーポンのステータスごとに、設定あり/なしが一覧で確認できる" />
              <StepList>
                <Step>設定したいステータスの行を開きます。</Step>
                <Step>テキストエリアに文章を入力します。「例文に戻す」で用意された例文に戻せます。</Step>
                <Step>「使わない(空にする)」を押すと、そのステータスではメッセージを入れないようにできます。</Step>
              </StepList>

              <SubHeading>審査員管理</SubHeading>
              <p>
                名前とメールアドレスを登録すると、ログイン情報を記載した案内メールが自動送信されます。
                審査員は届いたメールアドレスと初期パスワードでログインすると、審査だけが使える専用画面が
                表示されます。パスワードはログイン後に本人が変更できます。
              </p>
              <StepList>
                <Step>「審査員を追加」の欄に、審査員の名前とメールアドレスを入力します。</Step>
                <Step>「追加」を押します。登録が終わると一覧に「招待中」として表示されます。</Step>
              </StepList>
              <Shot
                src="/salon-manual/settings-judges.jpg"
                alt="審査員管理の一覧と審査員を追加する入力欄"
                caption="登録済みの審査員には「登録済み」バッジが付く(追加した直後は「招待中」と表示される)" />

              <TipBox>
                <li>複数の審査員で評価の目線をそろえたい場合は、審査項目のタイトルを具体的に設定しておくと評価がぶれにくくなります。</li>
                <li>ステータス変更時のメッセージをあらかじめ設定しておくと、選考結果の連絡を毎回書かずに送れます。</li>
              </TipBox>
            </section>

            <section id="judging" ref={(el) => (sectionRefs.current['judging'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoClipboardOutline className={Styles.sectionIcon} />
                審査
              </h2>
              <SectionLead>審査員として、応募者の資料を確認しながら評価を提出する画面です。</SectionLead>
              <p>提出した内容は「応募者管理」の詳細画面の「管理・集計」タブに集約されます。</p>

              <SubHeading>対象を絞り込む</SubHeading>
              <StepList>
                <Step>画面上部のタブで「応募の審査」と「サロン見学の審査」を切り替えます。</Step>
                <Step>応募の審査では、さらに選考段階(1次選考・2次選考…)で絞り込めます。</Step>
                <Step>「審査前」「審査済み」のタブで、自分がまだ評価していない学生だけに絞り込めます。</Step>
              </StepList>

              <SubHeading>評価者名を入力する</SubHeading>
              <p>
                会社の共有ログインで開いた場合、まだ評価者として識別されていないときは、最初に名前の入力を求められます
                (この名前はブラウザに保存される自己申告で、本人確認の仕組みではありません)。審査員専用ログインで
                開いた場合は、この入力は表示されません。
              </p>
              <StepList>
                <Step>「評価者名を入力してください」の欄に名前を入力します。</Step>
                <Step>「この名前で審査を始める」を押します。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/salon-manual/judging-start.jpg"
                  alt="応募の審査画面(評価者名の入力前)"
                  caption="学生情報の下にMy Cardが表示され、右側には選考フローの評価観点(審査項目のタイトル)が並ぶ"
                  hotspots={[{ n: 1, x: 70, y: 71 }, { n: 2, x: 58, y: 78 }]} />
                <Shot
                  src="/salon-manual/judging-visit.jpg"
                  alt="サロン見学の審査画面(評価者名の入力前)"
                  caption="「サロン見学の審査」タブ。選考段階がない代わりに、サロン見学用の評価観点が表示される"
                  hotspots={[{ n: 1, x: 70, y: 67 }, { n: 2, x: 58, y: 74 }]} />
              </div>

              <SubHeading>評価を入力する</SubHeading>
              <StepList>
                <Step>審査項目ごとに、◎(とても良い)〜×(気になる)・-(対象外)から選びます。必要に応じて項目ごとのメモも入力できます。</Step>
                <Step>「総合判断」で、今回の選考に対する総合的な評価を選びます。</Step>
                <Step>「評価コメント」(必須)に、良かった点・気になった点を入力します。</Step>
              </StepList>
              <Shot
                src="/salon-manual/judging-scores.jpg"
                alt="審査項目の評価入力画面(◎○△×-のボタン)"
                caption="審査項目ごとに評価と項目別メモを入力する画面" />
              <Shot
                src="/salon-manual/judging-comment.jpg"
                alt="総合判断と評価コメントの入力画面"
                caption="総合判断の選択肢と、必須の評価コメント入力欄" />

              <SubHeading>提出する</SubHeading>
              <StepList>
                <Step>内容を確認し、「下書き保存」または「評価を提出する」を押します。</Step>
                <Step>「次の学生へ自動で移動する」にチェックを入れておくと、提出後に自動で次の対象者へ切り替わります。</Step>
              </StepList>
              <Shot
                src="/salon-manual/judging-submitted.jpg"
                alt="評価を提出した直後の画面(審査前0・審査済み1)"
                caption="提出すると「審査前」の件数が減り、「審査済み」に移る" />

              <Caution>
                「下書き保存」は必須項目を入力していなくても保存できますが、「評価を提出する」には審査項目・
                総合判断・評価コメントの入力が必要です。
              </Caution>

              <TipBox>
                <li>複数人で審査する場合は、先に「応募設定」で審査項目のタイトルを決めておくと評価の基準がそろいます。</li>
                <li>「次の学生へ自動で移動する」を使うと、まとめて何人も審査するときの手間を減らせます。</li>
              </TipBox>
            </section>

            <section id="company" ref={(el) => (sectionRefs.current['company'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoSettingsOutline className={Styles.sectionIcon} />
                会社の設定・管理
              </h2>
              <SectionLead>会社の基本情報・ロゴ・通知の設定、ログアウトを行う画面です。</SectionLead>
              <p>ここで登録した情報は求人ページやアプリの表示に反映されます。</p>

              <Shot
                src="/salon-manual/company-setting.jpg"
                alt="設定・管理画面(基本情報・メール通知設定・LINE通知設定)"
                caption="会社ロゴ・企業名の基本情報、メール通知設定、LINE通知設定が並ぶ"
                hotspots={[{ n: 1, x: 61, y: 30 }]} />

              <SubHeading>会社ロゴ・企業名を変更する</SubHeading>
              <StepList>
                <Step>会社ロゴの鉛筆アイコンを押し、画像ファイルを選びます。</Step>
                <Step>企業名を入力します。</Step>
                <Step>「保存」を押します。</Step>
              </StepList>

              <SubHeading>メール通知先を変更する</SubHeading>
              <StepList>
                <Step>「メール通知設定」の鉛筆アイコンを押します。</Step>
                <Step>通知を受け取りたいメールアドレスを入力し、「保存」を押します。</Step>
              </StepList>
              <Shot
                src="/salon-manual/company-setting-mail-modal.jpg"
                alt="通知先アドレスの編集モーダル"
                caption="通知先アドレスの編集モーダル。未登録の場合は「未登録」と表示される" />

              <FeatureBox>
                <IconItem icon={<IoPersonAddOutline />} title="LINE通知設定">
                  会社の公式LINEアカウントと連携すると、メールと同じ内容をLINEでも受け取れます(操作は下記)。
                </IconItem>
                <IconItem icon={<IoListOutline />} title="ログアウト">
                  画面下部の「ログアウト」から、現在のアカウントからログアウトできます。
                </IconItem>
              </FeatureBox>

              <SubHeading>LINE通知を設定する</SubHeading>
              <StepList>
                <Step>「LINEと連携する」を押し、会社の公式LINEアカウントで連携します。</Step>
                <Step>連携が完了したら、「通知用 公式LINE」から通知を送るための公式LINEを追加します。</Step>
              </StepList>

              <TipBox>
                <li>応募やメッセージの通知を見逃したくない場合は、メール・LINEの両方を設定しておくと安心です。</li>
              </TipBox>
            </section>

            <section id="faq" ref={(el) => (sectionRefs.current['faq'] = el)} className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHelpCircleOutline className={Styles.sectionIcon} />
                よくある質問
              </h2>
              <SectionLead>自分で解決できそうにない場合は、運営(memorii works)まで、画面のスクリーンショットを添えてお問い合わせください。</SectionLead>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. ログインできません。</p>
                <p className={Styles.faqA}>
                  A. ログイン画面で「企業」タブが選ばれているかご確認ください。メールアドレス・パスワードに
                  誤りがない場合は、運営(memorii works)までお問い合わせください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 「応募者管理」「審査」のメニューが表示されません。</p>
                <p className={Styles.faqA}>
                  A. これらは契約内容によって表示が変わる機能です。表示されるべきメニューが見当たらない場合は、
                  運営(memorii works)までお問い合わせください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 履歴書タブに「履歴書情報の登録がありません」と表示されます。</p>
                <p className={Styles.faqA}>
                  A. 学生がアプリ側で履歴書情報を入力・提出していない場合に表示されます。学生に直接ご確認ください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 審査員を登録しても案内メールが届きません。</p>
                <p className={Styles.faqA}>
                  A. 迷惑メールフォルダをご確認のうえ、それでも見当たらない場合は登録したメールアドレスに
                  誤りがないか確認し、運営(memorii works)までお問い合わせください。
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>

      {showBackToTop && (
        <button
          type="button"
          className={Styles.backToTop}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="ページ上部へ戻る">
          <IoArrowUpOutline />
        </button>
      )}

      {zoom && (
        <div className={Styles.lightbox} role="dialog" aria-modal="true" aria-label={zoom.alt}>
          <div className={Styles.lightboxBg} onClick={() => setZoom(null)} />
          <button type="button" className={Styles.lightboxClose} onClick={() => setZoom(null)} aria-label="閉じる">
            <IoCloseOutline />
          </button>
          <img className={Styles.lightboxImg} src={zoom.src} alt={zoom.alt} />
        </div>
      )}
    </ZoomContext.Provider>
  );
};

export default SalonManual;
